import React from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  CancelOutlined, CheckCircleOutline, ErrorOutline, HourglassEmptyOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const STATUS_CHIP = {
  pending: { color: 'warning', Icon: HourglassEmptyOutlined },
  running: { color: 'info', Icon: HourglassEmptyOutlined },
  confirmed: { color: 'success', Icon: CheckCircleOutline },
  executed: { color: 'success', Icon: CheckCircleOutline },
  cancelled: { color: 'default', Icon: CancelOutlined },
  failed: { color: 'error', Icon: ErrorOutline },
  expired: { color: 'default', Icon: ErrorOutline },
};

/**
 * In-chat confirmation card for confirm-tier agent actions. Replaces any
 * browser confirm(): the user explicitly approves or cancels here.
 */
const ConfirmActionCard = ({ action, onConfirm, onCancel, disabled }) => {
  const { t } = useTranslation();
  const status = action?.status || 'pending';
  const chip = STATUS_CHIP[status] || STATUS_CHIP.pending;
  const isPending = status === 'pending';
  const isRunning = status === 'running';
  const cardId = `assistant-action-${action?.id}`;

  return (
    <Paper
      variant="outlined"
      role="group"
      aria-labelledby={`${cardId}-title`}
      sx={{
        p: 1.5,
        borderColor: isPending ? 'warning.main' : 'divider',
        bgcolor: isPending ? 'rgba(237, 108, 2, 0.06)' : 'background.paper',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <WarningAmberOutlined color={isPending ? 'warning' : 'disabled'} fontSize="small" aria-hidden="true" sx={{ mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={`${cardId}-title`} variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('assistant.confirm.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            {action?.summary}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {isPending ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => onConfirm(action.id)}
                  disabled={disabled}
                  aria-label={t('assistant.confirm.confirmAria', { action: action?.summary })}
                >
                  {t('assistant.confirm.confirm')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => onCancel(action.id)}
                  disabled={disabled}
                  aria-label={t('assistant.confirm.cancelAria', { action: action?.summary })}
                >
                  {t('assistant.confirm.cancel')}
                </Button>
              </>
            ) : (
              <Chip
                size="small"
                color={chip.color}
                icon={isRunning ? <CircularProgress size={12} color="inherit" /> : <chip.Icon fontSize="small" />}
                label={t(`assistant.confirm.status.${status}`)}
                aria-label={t(`assistant.confirm.status.${status}`)}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ConfirmActionCard;
